import { View, Text, Pressable, ScrollView } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import {
  ChevronLeft,
  Clock,
  CheckCircle,
  XCircle,
  User,
  Building2,
  Shield,
  AlertCircle,
} from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp, SlideInRight } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { useProfileStore } from '@/lib/state/profile-store';
import { useAuth } from '@/lib/supabase';
import { useMyStartup } from '@/lib/startup/use-my-startup';
import {
  useIncomingVerifications,
  useRespondToVerification,
} from '@/lib/supabase/portfolio-verification';
import { useAlert } from '@/components/AlertDialog';
import { handleError } from '@/lib/errors';
import { VerificationRequest } from '@/lib/state/inverio-store';
import { isWeb } from '@/lib/platform';
import { usePhoneShell } from '@/lib/useShell';
import { AppLayout, Breadcrumb } from '@/components/AppLayout';
import AnimatedPressable from '@/components/AnimatedPressable';

// Standard shadow styles
const cardShadow = {
  shadowColor: '#000',
  shadowOpacity: 0.05,
  shadowRadius: 8,
  shadowOffset: { width: 0, height: 2 },
  elevation: 2,
};

const getStatusConfig = (colors: ReturnType<typeof useThemeColors>): Record<
  VerificationRequest['status'],
  { label: string; icon: typeof Clock; color: string; bgColor: string }
> => ({
  pending: {
    label: 'Pending',
    icon: Clock,
    color: colors.amber.main,
    bgColor: colors.amber.light,
  },
  verified: {
    label: 'Verified',
    icon: CheckCircle,
    color: colors.traffic.green,
    bgColor: colors.traffic.greenLight,
  },
  rejected: {
    label: 'Rejected',
    icon: XCircle,
    color: colors.traffic.red,
    bgColor: colors.traffic.redLight,
  },
});

// Breadcrumbs for navigation
const BREADCRUMBS: Breadcrumb[] = [
  { label: 'Company Profile', href: '/(startup)/company' },
  { label: 'Verification Requests' },
];

export default function VerificationRequestsScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();
  const STATUS_CONFIG = getStatusConfig(colors);
  const startupProfile = useProfileStore((s) => s.startupProfile);

  // REAL claims, not the mock array this screen read for months.
  //
  // Both halves of portfolio verification were built and never connected:
  // onboarding wrote `portfolioClaims` to the investor's device, and this queue
  // read `getMockVerificationRequests()` on the founder's. A claim could never
  // travel between them, so the feature worked only in mock mode.
  //
  // Ownership resolves via membership (useMyStartup), never
  // startup_profiles.user_id — see the OWNERSHIP INVARIANT.
  const { user: authUser } = useAuth();
  const { startupId } = useMyStartup();
  const { data: claims = [] } = useIncomingVerifications(startupId ?? undefined);
  const respondMutation = useRespondToVerification();
  const { error: showError } = useAlert();

  // The screen's existing shape. `investorFirm` carries the SYNDICATE LEAD when
  // there is one: a founder often never met the individual participant — the
  // cheque and the cap-table line sit with the lead — so that is the name they
  // can actually recognise.
  const verificationRequests = claims.map((c) => ({
    id: c.id,
    investorId: c.investorUserId,
    investorName: c.investorName,
    investorFirm:
      c.investmentType === 'syndicate' && c.syndicateLeadName
        ? `via ${c.syndicateLeadName}`
        : undefined,
    claimId: c.id,
    requestDate: c.investmentDate ?? c.requestedAt,
    status: c.status,
    respondedAt: c.respondedAt,
  }));

  const pendingRequests = verificationRequests.filter((r) => r.status === 'pending');
  const resolvedRequests = verificationRequests.filter((r) => r.status !== 'pending');

  const respond = async (requestId: string, status: 'verified' | 'rejected') => {
    if (!startupId || !authUser?.id) return;
    try {
      await respondMutation.mutateAsync({
        id: requestId,
        startupId,
        authUserId: authUser.id,
        status,
      });
    } catch (err) {
      // RLS filters rather than errors, so the mutation turns a zero-row update
      // into a thrown error. Surfacing it is the point: this screen used to
      // mutate a local array and always "succeed".
      const { message } = handleError(err, { context: 'VerificationRequests.respond' });
      showError('Could not respond', message);
    }
  };

  const handleVerify = (requestId: string) => {
    void respond(requestId, 'verified');
  };

  const handleReject = (requestId: string) => {
    void respond(requestId, 'rejected');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <AppLayout title="Verification Requests" breadcrumbs={BREADCRUMBS}>
      <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
        <SafeAreaView edges={isWeb ? [] : ['top']} className="flex-1">
          {/* Header - Only show on mobile */}
          {phoneShell && (
            <Animated.View
              entering={FadeInDown.delay(100).duration(500)}
              className="px-5 pt-4 pb-4"
            >
              <View className="flex-row items-center gap-3">
                <Pressable
                  onPress={() => router.back()}
                  className="w-10 h-10 rounded-full items-center justify-center"
                  style={{ backgroundColor: colors.surface.primary }}
                >
                  <ChevronLeft size={24} color={colors.text.primary} />
                </Pressable>
                <View className="flex-1">
                  <Text
                    style={{ color: colors.text.primary }}
                    className="text-xl font-bold"
                  >
                    Verification Requests
                  </Text>
                  <Text
                    style={{ color: colors.text.secondary }}
                    className="text-sm"
                  >
                    Investors claiming to have invested in {startupProfile?.name}
                  </Text>
                </View>
              </View>
            </Animated.View>
          )}

        <ScrollView className="flex-1 px-5" showsVerticalScrollIndicator={false}>
          {/* Pending Requests */}
          {pendingRequests.length > 0 && (
            <Animated.View
              entering={FadeInUp.delay(200).duration(500)}
              className="mb-6"
            >
              <View className="flex-row items-center gap-2 mb-3">
                <AlertCircle size={16} color={colors.amber.main} />
                <Text
                  style={{ color: colors.text.primary }}
                  className="font-semibold"
                >
                  Pending ({pendingRequests.length})
                </Text>
              </View>

              <View className="gap-3">
                {pendingRequests.map((request, index) => (
                  <Animated.View
                    key={request.id}
                    entering={SlideInRight.delay(index * 100).duration(400)}
                  >
                    <View
                      className="p-4 rounded-xl"
                      style={{
                        backgroundColor: colors.surface.primary,
                        borderWidth: 2,
                        borderColor: colors.amber.light,
                      }}
                    >
                      <View className="flex-row items-start mb-3">
                        <View
                          className="w-12 h-12 rounded-full items-center justify-center mr-3"
                          style={{ backgroundColor: colors.primary[50] }}
                        >
                          <User size={24} color={colors.primary[600]} />
                        </View>
                        <View className="flex-1">
                          <Text
                            style={{ color: colors.text.primary }}
                            className="font-semibold text-base"
                          >
                            {request.investorName}
                          </Text>
                          {request.investorFirm && (
                            <View className="flex-row items-center gap-1 mt-1">
                              <Building2 size={12} color={colors.text.tertiary} />
                              <Text
                                style={{ color: colors.text.secondary }}
                                className="text-sm"
                              >
                                {request.investorFirm}
                              </Text>
                            </View>
                          )}
                          <Text
                            style={{ color: colors.text.tertiary }}
                            className="text-xs mt-1"
                          >
                            Requested {formatDate(request.requestDate)}
                          </Text>
                        </View>
                      </View>

                      <View
                        className="p-3 rounded-lg mb-4"
                        style={{ backgroundColor: colors.surface.secondary }}
                      >
                        <Text
                          style={{ color: colors.text.secondary }}
                          className="text-sm"
                        >
                          This investor claims to have invested in your company.
                          Verify if this is accurate.
                        </Text>
                      </View>

                      <View className="flex-row gap-3">
                        <AnimatedPressable
                          onPress={() => handleReject(request.id)}
                          className="flex-1 flex-row items-center justify-center gap-2 py-3 rounded-xl"
                          style={{
                            backgroundColor: colors.traffic.redLight,
                          }}
                        >
                          <XCircle size={18} color={colors.traffic.red} />
                          <Text
                            style={{ color: colors.traffic.red }}
                            className="font-semibold"
                          >
                            Reject
                          </Text>
                        </AnimatedPressable>
                        <AnimatedPressable
                          onPress={() => handleVerify(request.id)}
                          className="flex-1 flex-row items-center justify-center gap-2 py-3 rounded-xl"
                          style={{
                            backgroundColor: colors.traffic.green,
                          }}
                        >
                          <CheckCircle size={18} color="white" />
                          <Text className="font-semibold text-white">Verify</Text>
                        </AnimatedPressable>
                      </View>
                    </View>
                  </Animated.View>
                ))}
              </View>
            </Animated.View>
          )}

          {/* Resolved Requests */}
          {resolvedRequests.length > 0 && (
            <Animated.View
              entering={FadeInUp.delay(300).duration(500)}
              className="mb-6"
            >
              <View className="flex-row items-center gap-2 mb-3">
                <Shield size={16} color={colors.text.tertiary} />
                <Text
                  style={{ color: colors.text.secondary }}
                  className="font-semibold"
                >
                  History ({resolvedRequests.length})
                </Text>
              </View>

              <View className="gap-3">
                {resolvedRequests.map((request, index) => {
                  const statusConfig = STATUS_CONFIG[request.status];
                  const StatusIcon = statusConfig.icon;

                  return (
                    <Animated.View
                      key={request.id}
                      entering={SlideInRight.delay(index * 100).duration(400)}
                    >
                      <View
                        className="p-4 rounded-xl"
                        style={{
                          backgroundColor: colors.surface.primary,
                          borderWidth: 1,
                          borderColor: colors.border.light,
                        }}
                      >
                        <View className="flex-row items-center">
                          <View
                            className="w-10 h-10 rounded-full items-center justify-center mr-3"
                            style={{ backgroundColor: colors.surface.secondary }}
                          >
                            <User size={20} color={colors.text.tertiary} />
                          </View>
                          <View className="flex-1">
                            <Text
                              style={{ color: colors.text.primary }}
                              className="font-medium"
                            >
                              {request.investorName}
                            </Text>
                            {request.investorFirm && (
                              <Text
                                style={{ color: colors.text.tertiary }}
                                className="text-xs"
                              >
                                {request.investorFirm}
                              </Text>
                            )}
                          </View>
                          <View
                            className="flex-row items-center gap-1 px-3 py-1.5 rounded-full"
                            style={{ backgroundColor: statusConfig.bgColor }}
                          >
                            <StatusIcon size={14} color={statusConfig.color} />
                            <Text
                              style={{ color: statusConfig.color }}
                              className="text-xs font-semibold"
                            >
                              {statusConfig.label}
                            </Text>
                          </View>
                        </View>
                        {request.respondedAt && (
                          <Text
                            style={{ color: colors.text.tertiary }}
                            className="text-xs mt-2 ml-13"
                          >
                            Responded {formatDate(request.respondedAt)}
                          </Text>
                        )}
                      </View>
                    </Animated.View>
                  );
                })}
              </View>
            </Animated.View>
          )}

          {/* Empty State */}
          {verificationRequests.length === 0 && (
            <Animated.View
              entering={FadeInUp.delay(200).duration(500)}
              className="flex-1 items-center justify-center py-20"
            >
              <View
                className="w-20 h-20 rounded-full items-center justify-center mb-4"
                style={{ backgroundColor: colors.surface.primary }}
              >
                <Shield size={40} color={colors.text.tertiary} />
              </View>
              <Text
                style={{ color: colors.text.primary }}
                className="text-lg font-semibold text-center mb-2"
              >
                No Verification Requests
              </Text>
              <Text
                style={{ color: colors.text.secondary }}
                className="text-sm text-center px-8"
              >
                When investors claim to have invested in your company, you'll see
                their requests here for verification.
              </Text>
            </Animated.View>
          )}

          <View className="h-8" />
        </ScrollView>
      </SafeAreaView>
    </View>
    </AppLayout>
  );
}
